import React, { useRef, useCallback } from 'react';
import { useChatContext } from '../ChatContext';
import { ChatFooterContainer, ChatInput, ChatFooterButtonContainer, ResetButton, SendButton } from './styles';

export const ChatFooter = ({
  handleSendClick,
  handleReset
}: {
  handleSendClick: () => void;
  handleReset: () => void;
}): JSX.Element => {
  const { inputValue, setInputValue, messages, isLoading, setIsInputFocused } = useChatContext();
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const handleFocus = useCallback(() => {
    setIsInputFocused(true);

    // Scroll fixes for Safari iOS
    for (let i = 0; i < 200; i = i + 10) {
      setTimeout(() => {
        window.scrollTo({ top: 0 });
      }, i);
    }
  }, [setIsInputFocused]);

  const handleBlur = useCallback(() => {
    setIsInputFocused(false);
  }, [setIsInputFocused]);

  return (
    <ChatFooterContainer ref={containerRef}>
      <ChatInput
        ref={inputRef}
        inputMode="text"
        placeholder="What are you looking for?"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            if (!isLoading && inputValue.trim().length > 0) {
              handleSendClick();
            }
          }
        }}
      />
      <ChatFooterButtonContainer>
        <ResetButton
          disabled={isLoading || messages.length === 0}
          onClick={handleReset}
          aria-label="Reset Conversation"
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M10.746 8.106H8.028C7.86887 8.106 7.71626 8.16921 7.60374 8.28174C7.49121 8.39426 7.428 8.54687 7.428 8.706C7.428 8.86513 7.49121 9.01774 7.60374 9.13026C7.71626 9.24279 7.86887 9.306 8.028 9.306H9.468C8.80617 9.99764 7.95264 10.4756 7.0171 10.6784C6.08155 10.8813 5.10673 10.7998 4.21787 10.4443C3.32901 10.0889 2.56672 9.47586 2.02895 8.6839C1.49119 7.89195 1.20251 6.95728 1.2 6C1.2 5.84087 1.13679 5.68826 1.02426 5.57574C0.911742 5.46321 0.75913 5.4 0.6 5.4C0.44087 5.4 0.288258 5.46321 0.175736 5.57574C0.0632141 5.68826 0 5.84087 0 6C0.003172 7.17168 0.349333 8.31679 0.995762 9.29402C1.64219 10.2712 2.5606 11.0378 3.63765 11.4991C4.71469 11.9605 5.90324 12.0963 7.05661 11.89C8.20998 11.6836 9.2777 11.1441 10.128 10.338V11.4C10.128 11.5591 10.1912 11.7117 10.3037 11.8243C10.4163 11.9368 10.5689 12 10.728 12C10.8871 12 11.0397 11.9368 11.1523 11.8243C11.2648 11.7117 11.328 11.5591 11.328 11.4V8.7C11.3265 8.54498 11.2651 8.39655 11.1566 8.28582C11.0481 8.17509 10.901 8.11064 10.746 8.106ZM6 0C4.46183 0.00438732 2.98409 0.599348 1.872 1.662V0.6C1.872 0.44087 1.80879 0.288258 1.69626 0.175736C1.58374 0.0632141 1.43113 0 1.272 0C1.11287 0 0.960258 0.0632141 0.847736 0.175736C0.735214 0.288258 0.672 0.44087 0.672 0.6V3.3C0.672 3.45913 0.735214 3.61174 0.847736 3.72426C0.960258 3.83679 1.11287 3.9 1.272 3.9H3.972C4.13113 3.9 4.28374 3.83679 4.39626 3.72426C4.50879 3.61174 4.572 3.45913 4.572 3.3C4.572 3.14087 4.50879 2.98826 4.39626 2.87574C4.28374 2.76321 4.13113 2.7 3.972 2.7H2.532C3.19348 2.00872 4.04645 1.5309 4.98144 1.32786C5.91642 1.12483 6.89075 1.20585 7.77936 1.56052C8.66798 1.91519 9.43032 2.52733 9.96854 3.31836C10.5068 4.1094 10.7963 5.04323 10.8 6C10.8 6.15913 10.8632 6.31174 10.9757 6.42426C11.0883 6.53679 11.2409 6.6 11.4 6.6C11.5591 6.6 11.7117 6.53679 11.8243 6.42426C11.9368 6.31174 12 6.15913 12 6C12 5.21207 11.8448 4.43185 11.5433 3.7039C11.2417 2.97595 10.7998 2.31451 10.2426 1.75736C9.68549 1.20021 9.02405 0.758251 8.2961 0.456723C7.56815 0.155195 6.78793 0 6 0Z"
              fill="#333333"
            />
          </svg>
          RESET
        </ResetButton>
        <SendButton
          disabled={isLoading || !inputValue?.trim().length}
          onClick={handleSendClick}
          aria-label="Send message"
        >
          SEND
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M3.34211 10.964C3.08583 10.7078 3.08583 10.2922 3.34211 10.036L7.37807 6L3.34211 1.96404C3.08583 1.70776 3.08583 1.29224 3.34211 1.03596C3.5984 0.779679 4.01391 0.779679 4.27019 1.03596L8.77019 5.53596C9.02647 5.79224 9.02647 6.20776 8.77019 6.46404L4.27019 10.964C4.01391 11.2203 3.5984 11.2203 3.34211 10.964Z"
              fill="white"
            />
          </svg>
        </SendButton>
      </ChatFooterButtonContainer>
    </ChatFooterContainer>
  );
};
